import { useMemo, useState } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import { Box, useMediaQuery, CircularProgress, Alert } from '@mui/material'
import { t } from 'i18next'
import { useImpersonation } from '../../context/impersonationContext'
import { useGetImpersonationUsersQuery } from '../../hooks/api/impersonation'
import { ImpersonationUser } from '../../types/api/impersonation'
import Title from '../../components/common/title'
import CustomTable from '../../components/common/customTable'
import SearchFilters from './components/searchFilters'
import RowActions from './components/rowActions'
import {
  NameTitle,
  NameBody,
  CompanyTitle,
  CompanyBody,
  RegionalAcqTitle,
  RegionalAcqBody,
  ImpersonateTitle,
} from './components/tableColumns'

const Personification = () => {
  const { isLoading: impersonationLoading, error: impersonationError, getImpersonationRegionCode } = useImpersonation()
  const isMobile = useMediaQuery('(max-width:1000px)')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)

  const activeRegionCode = getImpersonationRegionCode()

  const apiParams = useMemo(() => ({
    page: page.toString(),
    sort: { id: 'email', desc: false },
    filter: searchTerm ? { query: searchTerm } : undefined,
    paginated: 'true'
  }), [page, searchTerm])

  const { 
    data: apiResponse, 
    isLoading, 
    error 
  } = useGetImpersonationUsersQuery(apiParams, activeRegionCode)

  const data = apiResponse?.data || []
  const totalPages = parseInt(apiResponse?.metadata?.last_page || '1')
  const totalCount = parseInt(apiResponse?.metadata?.total || '0')

  const columns = useMemo<MRT_ColumnDef<ImpersonationUser>[]>(
    () => [
      {
        accessorKey: 'first_name',
        header: 'Nom',
        Header: NameTitle,
        enableSorting: false,
        Cell: NameBody,
      },
      {
        accessorKey: 'company_name',
        header: 'Entreprise',
        Header: CompanyTitle,
        enableSorting: false,
        Cell: CompanyBody,
      },
      {
        accessorKey: 'region_name',
        header: 'ACQ régionale',
        Header: RegionalAcqTitle,
        enableSorting: false,
        Cell: RegionalAcqBody,
      },
      {
        accessorKey: 'auth0_id',
        header: '',
        Header: ImpersonateTitle,
        enableSorting: false,
        Cell: RowActions,
      },
    ],
    [isMobile]
  )

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
    setPage(1)
  }

  if (isLoading || impersonationLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || impersonationError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">
          {error?.message || impersonationError || 'Une erreur est survenue lors du chargement des données.'}
        </Alert>
      </Box>
    )
  }

  return (
    <>
      <Title 
        sx={{ 
          mb: '48px',
          fontFamily: 'DIN',
          fontWeight: 700,
          fontSize: '40px',
          lineHeight: '42px',
          letterSpacing: '0px',
          textTransform: 'uppercase'
        }}
      >
        {t('pages.personification.title')}
      </Title>
      
      <SearchFilters onSearch={handleSearch} resultsCount={totalCount} />

      <Box sx={{ mb: '24px' }}>
        <CustomTable
          data={data}
          columns={columns}
          enableSorting={false}
          muiTableBodyRowProps={{ hover: false }}
          enableSortingRemoval={false}
          enableMultiSort={false}
          enableSelectAll={false}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              size: 100,
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxWidth: '100%',
              ...(isMobile && {
                minWidth: '800px',
              }),
            },
          }}
          propsPagination={{
            page: page,
            count: totalPages,
            onChange: (e, index) => {
              setPage(index)
            },
          }}
        />
      </Box>
    </>
  )
}

export default Personification
